import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import bbox from "@turf/bbox";
import { Basemap } from "components/basemap/basemap";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionTooltipButton } from "components/permissions/permissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { deleteClip } from "data/mutations-common";
import { fetchClipSummary } from "data/queries-common";
import {
  Copy,
  Download,
  Eye,
  Gem,
  Pencil,
  Receipt,
  Trash,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import { FillLayer, Layer, LineLayer, MapRef, Source } from "react-map-gl";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { openDownloadLinkCallback } from "utils/download";
import { resolveIdentityId } from "utils/identity";
import { hasPremiumData } from "../clip-ui/clip-menu";
import { ClipConfiguration } from "../clip-ui/models";
import { SummaryTable } from "../clip-ui/steps/step-4-summary/summary-table";
import {
  formatClipArea,
  formatCRS,
  formatFileFormat,
  hasLiveQuote,
  isViewOnly,
} from "../clip-utils";

const MAP_PRIMARY_COLOR = "#1976d2";

interface ClipDetailSidebarProps {
  clip: ClipConfiguration;
  onClose: () => void;
}

export const ClipDetailSidebar = ({
  clip,
  onClose,
}: ClipDetailSidebarProps) => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const mapRef = useRef<MapRef>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { enqueueInfoSnackbar } = useCustomSnackbars();
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const clipHasPremiumDataForDownload = hasPremiumData(clip);
  const viewOnly = isViewOnly(clip.status);

  const clipDeleteMutation = useMutation({
    mutationFn: () => deleteClip(clip.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.clipsKey] });
      setDeleteModalIsOpen(false);
      onClose();
      enqueueInfoSnackbar("Clip deleted successfully", "success");
    },
    onError: (error: Error) => {
      enqueueInfoSnackbar(error.message, "error");
    },
  });

  const canDeleteClip =
    clip.status !== "Processing" && clip.status !== "Quoting";

  // A sold clip reads its frozen receipt, so its figures are always current.
  const showFigures =
    clip.status === "Processing" ||
    clip.status === "Completed" ||
    hasLiveQuote(clip);

  
  const quoteExpired = clip.status === "Quoted" && !hasLiveQuote(clip);

  const clipSummaryQuery = useQuery({
    queryKey: [QueryKeys.clipSummaryKey, identityId, clip.id],
    queryFn: () => fetchClipSummary(clip.id),
    enabled: clip.status !== "Draft",
  });

  const [minLng, minLat, maxLng, maxLat] = clip.geometry
    ? bbox(clip.geometry)
    : [133.7751 - 10, -25.2744 - 10, 133.7751 + 10, -25.2744 + 10]; // Center of Australia with padding

  const fillLayer: FillLayer = {
    id: "clip-fill",
    type: "fill",
    paint: {
      "fill-color": MAP_PRIMARY_COLOR,
      "fill-opacity": 0.3,
    },
  };

  const lineLayer: LineLayer = {
    id: "clip-outline",
    type: "line",
    paint: {
      "line-color": MAP_PRIMARY_COLOR,
      "line-width": 2,
    },
  };

  const author = clip.authorName || clip.developer || "";
  const purchaser =
    clip.purchaserName ||
    clip.authorName ||
    (clip.status === "Completed" ? clip.developer : "") ||
    "";
  const isPremiumClip = hasPremiumData(clip);

  const handleCopyClipId = () => {
    navigator.clipboard.writeText(clip.id);
  };

  if (clipSummaryQuery.isEnabled && clipSummaryQuery.isPending) {
    return (
      <Card className="w-full h-full overflow-auto flex flex-col shrink-0">
        <CardContent className="p-2 flex flex-col gap-4">
          <div className="flex justify-between items-start">
            <Skeleton className="h-10 w-50" />
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X />
            </Button>
          </div>
          <Skeleton className="h-4 w-37" />
          <Skeleton className="h-25 w-full" />
          <Skeleton className="h-50 w-full" />
          <Skeleton className="h-75 w-full" />
          <Skeleton className="h-25 w-full" />
          <div className="flex gap-2">
            <Skeleton className="h-9 flex-1" />
            <Skeleton className="h-9 flex-1" />
            <Skeleton className="h-9 flex-1" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="w-full h-full overflow-auto flex flex-col shrink-0">
        <CardContent className="p-2 flex flex-col gap-2">
          <ReactQueryErrorWrapper queries={[clipSummaryQuery]} />

          {/* Header */}
          <div className="flex justify-between items-start gap-2">
            <h2 className="text-xl font-bold truncate min-w-0">{clip.name}</h2>
            <div className="flex gap-2 items-center shrink-0">
              {isPremiumClip && <Gem className="text-primary" size={20} />}
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-sm text-muted-foreground">
              Clip ID: {clip.id}
            </span>
            <Button variant="ghost" size="icon" onClick={handleCopyClipId}>
              <Copy />
            </Button>
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <p className="text-xs text-muted-foreground">Format</p>
              <p className="text-sm font-medium">
                {formatFileFormat(clip.format)}
              </p>
            </div>
            <div className="col-span-4">
              <p className="text-xs text-muted-foreground">Datum</p>
              <p className="text-sm font-medium">{formatCRS(clip.crs)}</p>
            </div>
            <div className="col-span-4">
              <p className="text-xs text-muted-foreground">Clip Area</p>
              <p className="text-sm font-medium">
                {formatClipArea(clip.geometry)}
              </p>
            </div>
            <div className="col-span-6 min-w-0">
              <p className="text-xs text-muted-foreground">Client Reference</p>
              <p className="text-sm font-medium truncate">
                {clip.clientReference || "-"}
              </p>
            </div>
            <div className="col-span-6">
              <p className="text-xs text-muted-foreground">Status</p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{clip.status}</p>
                {quoteExpired && (
                  <Badge variant="destructive">Quote has expired</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Note */}
          {clip.notes && (
            <>
              <p className="text-sm font-semibold">Note</p>
              <p className="text-sm text-muted-foreground">{clip.notes}</p>
            </>
          )}

          {/* Data Section */}
          <p className="text-sm font-bold">Data</p>

          {clipSummaryQuery.data ? (
            <SummaryTable
              quoteSummary={clipSummaryQuery.data}
              showFeatures={showFigures}
              showRate={showFigures}
              showCredits={showFigures}
              showTotal={showFigures}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Quote summary not available for draft clips
            </p>
          )}

          {/* Map */}
          {clip.geometry && (
            <div className="w-full h-75 rounded-sm overflow-hidden border border-border">
              <Basemap
                key={clip.id}
                // @ts-ignore
                mapRef={mapRef}
                attributionControl={false}
                onZoom={() => {}}
                style={{
                  width: "100%",
                  height: "100%",
                }}
                interactive={true}
                bbox={[minLng, minLat, maxLng, maxLat]}
                bboxPadding={40}
              >
                <Source type="geojson" data={clip.geometry}>
                  <Layer {...fillLayer} />
                  <Layer {...lineLayer} />
                </Source>
              </Basemap>
            </div>
          )}

          {/* Timeline */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3">
              <p className="text-xs text-muted-foreground">Created</p>
              <p className="text-sm">
                {formatTimestamp(new Date(clip.date).getTime())}
              </p>
            </div>
            <div className="col-span-3">
              <p className="text-xs text-muted-foreground">Modified</p>
              <p className="text-sm">
                {clip.lastEditedAt
                  ? formatTimestamp(clip.lastEditedAt * 1000)
                  : formatTimestamp(new Date(clip.date).getTime())}
              </p>
            </div>
            <div className="col-span-3">
              <p className="text-xs text-muted-foreground">Purchased</p>
              <p className="text-sm">
                {clip.purchasedOn
                  ? formatTimestamp(clip.purchasedOn * 1000)
                  : clip.status === "Completed"
                    ? formatTimestamp(new Date(clip.date).getTime())
                    : "-"}
              </p>
            </div>
            <div className="col-span-3">
              <p className="text-xs text-muted-foreground">Completed</p>
              <p className="text-sm">
                {clip.completedOn
                  ? formatTimestamp(clip.completedOn * 1000)
                  : clip.status === "Completed"
                    ? formatTimestamp(new Date(clip.date).getTime())
                    : "-"}
              </p>
            </div>
            <div className="col-span-6">
              <p className="text-xs text-muted-foreground">Author</p>
              <p className="text-sm">{author || "-"}</p>
            </div>
            <div className="col-span-6">
              <p className="text-xs text-muted-foreground">Purchaser</p>
              <p className="text-sm">{purchaser || "-"}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-end gap-2">
            <PermissionTooltipButton
              permissionKeys={[PermissionsKeys.clipDelete]}
              additionalDisabled={!canDeleteClip}
              onClick={() => setDeleteModalIsOpen(true)}
              variant="destructive"
            >
              <Trash />
              Delete
            </PermissionTooltipButton>
            <ButtonGroup className="flex-1">
              <Button
                onClick={() => {
                  history.push(`${Routes.clipUi}?clip_id=${clip.id}`);
                }}
              >
                {viewOnly ? <Eye /> : <Pencil />}
                {viewOnly ? "View" : "Edit"}
              </Button>
              <Button
                disabled={!clip.invoiceId}
                onClick={() => {
                  if (clip.invoiceUrl) {
                    window.open(clip.invoiceUrl, "_blank");
                  }
                }}
              >
                <Receipt />
                Invoice
              </Button>
              {clipHasPremiumDataForDownload ? (
                <PermissionTooltipButton
                  permissionKeys={[PermissionsKeys.premiumDataRead]}
                  additionalDisabled={clip.status !== "Completed"}
                  onClick={openDownloadLinkCallback(
                    `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                      clip.id
                    }/output`,
                  )}
                >
                  <Download />
                  Download
                </PermissionTooltipButton>
              ) : (
                <Button
                  disabled={clip.status !== "Completed"}
                  onClick={openDownloadLinkCallback(
                    `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                      clip.id
                    }/output`,
                  )}
                >
                  <Download />
                  Download
                </Button>
              )}
            </ButtonGroup>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isLoading={clipDeleteMutation.isPending}
        isError={clipDeleteMutation.isError}
        open={deleteModalIsOpen}
        header="Delete this clip?"
        body="This clip will be permanently removed from your organisation. You won't be able to recover its content"
        leftButtonText="CANCEL"
        leftButtonColor="primary"
        rightButtonText="DELETE CLIP"
        rightButtonColor="error"
        handleLeftButton={() => setDeleteModalIsOpen(false)}
        handleRightButton={() => clipDeleteMutation.mutate()}
        handleClose={() => setDeleteModalIsOpen(false)}
        type="Clip"
      />
    </>
  );
};
